import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Loader from "../../Components/Loader";
import "../../Styles/AdminDetails.css"; // Reusing existing styles

function AdminFaculty() {
  const { adminId } = useParams();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState("");
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [facultyToDelete, setFacultyToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        // First, get admin details to display admin name
        const adminResponse = await axios.get(
          `https://api.uniums.com/api/dashboard/admin/${adminId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (adminResponse.data && adminResponse.data.data) {
          setAdminName(adminResponse.data.data.name);
        }

        // Then, get faculty for this admin
        const response = await axios.get(
          `https://api.uniums.com/api/dashboard/${adminId}/faculty`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data && response.data.data) {
          setFaculty(response.data.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching faculty data:", error);
        setError("Failed to load faculty data. Please try again later.");
        setLoading(false);
      }
    };

    fetchFaculty();
  }, [adminId]);

  const handleDeleteClick = (facultyMember) => {
    setFacultyToDelete(facultyMember);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setFacultyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!facultyToDelete) return;
    
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setDeleteError("No authentication token found");
        setDeleteLoading(false);
        return;
      }

      // Call API to delete the faculty member
      await axios.delete(
        `https://api.uniums.com/api/dashboard/faculty/${facultyToDelete._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Remove deleted faculty member from the state
      setFaculty(faculty.filter(f => f._id !== facultyToDelete._id));
      setShowDeleteModal(false);
      setFacultyToDelete(null);
    } catch (error) {
      console.error("Error deleting faculty member:", error);
      setDeleteError("Failed to delete faculty member. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Loader />
        <div className="admin-details-loading">Loading faculty details...</div>
      </>
    );
  }

  if (error) {
    return <div className="admin-details-error">{error}</div>;
  }

  return (
    <div className="admin-details-main-container">
      <h2 className="admin-details-header-title">
        Faculty under {adminName || "Admin"}
      </h2>
      {faculty.length === 0 ? (
        <div className="admin-details-no-data">
          No faculty found for this admin.
        </div>
      ) : (
        <div className="admin-details-table-wrapper">
          <table className="admin-details-data-table">
            <thead>
              <tr>
                <th className="admin-details-table-header">Name</th>
                <th className="admin-details-table-header">Email</th>
                <th className="admin-details-table-header">Employee Code</th>
                <th className="admin-details-table-header">Gender</th>
                <th className="admin-details-table-header">Status</th>
                <th className="admin-details-table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {faculty.map((facultyMember) => (
                <tr
                  key={facultyMember._id}
                  className="admin-details-table-row"
                >
                  <td className="admin-details-table-cell">
                    {facultyMember.name}
                  </td>
                  <td className="admin-details-table-cell">
                    {facultyMember.email}
                  </td>
                  <td className="admin-details-table-cell">
                    {facultyMember.employeeCode || "N/A"}
                  </td>
                  <td className="admin-details-table-cell">
                    {facultyMember.gender || "N/A"}
                  </td>
                  <td className="admin-details-table-cell">
                    <span
                      className={`admin-details-status-badge ${
                        facultyMember.active
                          ? "admin-details-status-active"
                          : "admin-details-status-inactive"
                      }`}
                    >
                      {facultyMember.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="admin-details-action-cell">
                    <button
                      className="admin-details-delete-button"
                      onClick={() => handleDeleteClick(facultyMember)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-container">
            <div className="admin-modal-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="admin-modal-body">
              <p>
                Are you sure you want to delete the faculty member "{facultyToDelete?.name}"?
                This action cannot be undone.
              </p>
              {deleteError && (
                <div className="admin-modal-error">{deleteError}</div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-modal-cancel-button"
                onClick={handleCancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="admin-modal-confirm-button"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminFaculty;